import { errorService } from "../../../../../comun/services/ErrorService";
import {
  fx_createVerificarCodigoRequest,
  VerificarCodigoRequest,
} from "../../../../../modulos/seguridad/password/request/VerificarCodigoRequest";
import {
  parsePasswordResetError,
  passwordResetErrorMessage,
} from "../../../../../modulos/seguridad/password/response/PasswordResetErrorResponse";
import { verificarCodigoGrpcService as verificarCodigoService } from "../../../../../modulos/seguridad/password/services/VerificarCodigoGrpcService";
import { CardConnectRender } from "../../../../base/card/CardConnectRender";
import { CardBody } from "../../../../definitions/card/CardBody";
import { CardFooter } from "../../../../definitions/card/CardFooter";
import { CardHeader } from "../../../../definitions/card/CardHeader";
import { TextConfig } from "../../../../definitions/form/inputs/config/TextConfig";
import { TextField } from "../../../../implementations/form/text/TextField";

/** Código OTP: exactamente 6 dígitos. */
const CODIGO_REGEX = /^\d{6}$/;

export type VerificarCodigoCardConfig = {
  /** Email al que se envió el código (se muestra y viaja en el request). */
  email: string;
  /** Código válido: trae el `resetToken` y su expiración (Unix ms). */
  onVerificado: (resetToken: string, expiraEn: number) => void;
  /** Hay que pedir un código nuevo (venció, se bloqueó, o el usuario volvió). */
  onVolverAlPaso1: () => void;
};

/** Paso 2 del flujo "olvidé mi contraseña": canjea el código OTP por el
 *  `resetToken`. */
export class VerificarCodigoCardRender extends CardConnectRender {
  private readonly _config: VerificarCodigoCardConfig;
  private readonly _request: VerificarCodigoRequest;
  private readonly _codigoField: TextField<VerificarCodigoRequest>;

  constructor(config: VerificarCodigoCardConfig) {
    super();
    this._config = config;
    this._request = fx_createVerificarCodigoRequest(config.email);
    this._codigoField = this._createCodigoField();
    this._backAction = () => this._config.onVolverAlPaso1();
  }

  protected getCardHeader(): CardHeader {
    return {
      title: "Ingresá el código",
      subtitle: `Código de 6 dígitos enviado a ${this._config.email}`,
    };
  }

  protected getCardBody(): CardBody {
    return {
      additionalCssClass: "",
      content: this._codigoField.render(),
    };
  }

  protected getCardFooter(): CardFooter | null {
    return {
      additionalCssClass: "",
      content: `<button class="sx-btn sx-btn-primary sx-btn-block" type="button" id="codigo-submit-${this._cardId}">Verificar código</button>`,
    };
  }

  protected afterConnect(): void {
    this._codigoField.connect();

    // Atributos propios de un OTP que el template genérico de `TextField` no
    // expone; se fijan por DOM para no tocar un template compartido.
    const input = document.getElementById(
      `input-${this._codigoField.getId()}`,
    ) as HTMLInputElement | null;
    if (input) {
      input.maxLength = 6;
      input.inputMode = "numeric";
    }
    input?.focus();

    this._submitButton = document.getElementById(
      `codigo-submit-${this._cardId}`,
    ) as HTMLButtonElement;
    this._submitButton.addEventListener("click", this._onSubmitClick);
  }

  protected beforeDispose(): void {
    this._submitButton?.removeEventListener("click", this._onSubmitClick);
    this._codigoField.dispose();
  }

  private _submitButton: HTMLButtonElement | null = null;
  /** Error del servidor para el campo (se limpia al editar). */
  private _serverError: string | null = null;

  private _createCodigoField(): TextField<VerificarCodigoRequest> {
    const config: TextConfig<VerificarCodigoRequest> = {
      get(request) {
        return request.codigo;
      },
      set(request, newValue) {
        request.codigo = newValue;
      },
      label: "Código",
      type: "text",
      placeholder: "000000",
      description: "Los 6 dígitos que te llegaron por email",
      icon: "ph-password",
      autocomplete: "one-time-code",
    };
    const field = new TextField<VerificarCodigoRequest>(this._request, config, {
      isRequired: true,
      custom: (value) => {
        if (this._serverError) return this._serverError;
        return CODIGO_REGEX.test(value)
          ? null
          : "Ingresá los 6 dígitos del código";
      },
    });
    field.setOnChange(() => {
      this._serverError = null;
    });
    return field;
  }

  private _onSubmitClick = (): void => {
    this._codigoField.showError();
    if (this._codigoField.getError()) {
      this._codigoField.focus();
      return;
    }

    this._setBusy(true);
    verificarCodigoService
      .verificarAsync(this._request)
      .then((response) => {
        this._config.onVerificado(response.resetToken, response.expiraEn);
      })
      .catch((error: unknown) => {
        this._handleError(error);
      });
  };

  /** `CODIGO_INVALIDO` deja reintentar en el mismo paso; expirado y bloqueado
   *  obligan a pedir un código nuevo desde el paso 1. */
  private _handleError(error: unknown): void {
    const parsed = parsePasswordResetError(error);

    switch (parsed?.code) {
      case "CODIGO_INVALIDO":
        this._serverError = parsed.message;
        this._codigoField.showError();
        this._codigoField.focus();
        this._setBusy(false);
        return;

      case "CODIGO_EXPIRADO":
      case "CODIGO_BLOQUEADO":
        errorService.addWarning("Código no válido", parsed.message);
        this._config.onVolverAlPaso1();
        return;

      default:
        errorService.addError(
          "No se pudo verificar el código",
          parsed?.message ?? passwordResetErrorMessage(error),
        );
        this._setBusy(false);
    }
  }

  private _setBusy(isBusy: boolean): void {
    if (this._submitButton) this._submitButton.disabled = isBusy;
    if (isBusy) this._codigoField.disabled();
    else this._codigoField.enabled();
  }
}
